import { withDependencies } from '@wix/thunderbolt-ioc'
import type { ModuleLoader } from '@wix/thunderbolt-renderer-utils'
import { ESMLoadRegistrarSymbol, ReactTemplateSymbol } from './symbols'
import type { ESMLoaderFactory, ESMLoadRegistrar, ReactTemplate } from './types'
import * as signals from '@preact/signals-react'

function createModuleUrl(code: string) {
	const blob = new Blob([code], { type: 'text/javascript' })
	return URL.createObjectURL?.(blob) || ''
}

export const ESMLoaderFactoryFactory = withDependencies(
	[ReactTemplateSymbol, ESMLoadRegistrarSymbol],
	(reactTemplate: ReactTemplate, esmLoadRegistrar: ESMLoadRegistrar): ESMLoaderFactory => {
		let importMap: Record<string, string> = {}

		if (typeof window !== 'undefined') {
			(window as any).signals = signals
		}

		const defaultImportMap = {
			'react': createModuleUrl(reactTemplate.getReactModuleCode('window.React')),
			'react-dom': createModuleUrl(reactTemplate.getReactDOMModuleCode('window.ReactDOM')),
			'react/jsx-runtime': createModuleUrl(reactTemplate.getReactJsxRuntimeCode()),
			'react/jsx-dev-runtime': createModuleUrl(reactTemplate.getReactJsxDevRuntimeCode()),
			'@preact/signals-react': createModuleUrl(reactTemplate.getPreactSignalsReactModuleCode('window.signals')),
		}

		const updateImportMap = (dependencies: Record<string, string>) => {
			if (typeof window === 'undefined') {
				return
			}

			importMap = {
				...importMap,
				...dependencies,
			}
		}

		const createImportMap = () => {
			updateImportMap(defaultImportMap)

			const mapScriptId = 'builder-module-loader-importmap'
			let mapScript = document.getElementById(mapScriptId) as HTMLScriptElement

			if (!mapScript) {
				mapScript = document.createElement('script')
				mapScript.id = mapScriptId
				mapScript.type = 'importmap'
				document.head.appendChild(mapScript)
			}

			mapScript.textContent = JSON.stringify({ imports: importMap })
		}

		let moduleLoader: ModuleLoader | null = null

		return ({ scriptsCache, defaultDependencies = {} }) => {
			if (Object.keys(defaultDependencies).length > 0) {
				updateImportMap(defaultDependencies as Record<string, string>)
			}

			if (!moduleLoader) {
				moduleLoader = {
					prefetchScript: (_url) => {
						// not implemented for esm
					},
					loadModule: async (url) => {
						createImportMap()

						const cached = scriptsCache[url]
						if (cached) {
							return cached
						}

						return esmLoadRegistrar.loadModuleWithCache(url, async () => {
							const module = await import(/* webpackIgnore: true */ url)
							scriptsCache[url] = module
							return module
						})
					},
					updateImportMap,
				}
			}
			return moduleLoader
		}
	}
)
